import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, eur, imgUrl } from "@/lib/api";
import { ApproveDialog } from "@/pages/admin/ApproveDialog";

const STATUS = { new: ["Nauja", "text-amber-400"], approved: ["Patvirtinta, laukia apmokėjimo", "text-sky-400"], paid: ["Apmokėta", "text-emerald-400"], rejected: ["Atmesta", "text-red-400"], closed: ["Uždaryta", "text-slate-400"] };

const Customer = ({ c }) => (
  <div className="text-slate-400 text-xs space-y-0.5">
    {c.customer_type === "company" && <div className="text-slate-200">{c.company} · Įm. kodas: {c.company_code}{c.vat_code && ` · PVM: ${c.vat_code}`}</div>}
    <div className="text-slate-200">{c.name}</div>
    <div>{c.email} · {c.phone}</div>
    <div>{c.address}, {c.city} {c.postal_code}, {c.country}</div>
  </div>
);

const Card = ({ q, onApprove, onStatus, onZoom }) => (
  <div data-testid={`inquiry-card-${q.id}`} className="border border-[#3B4157] bg-[#292D3C] p-5 grid lg:grid-cols-[1fr_1fr_auto] gap-6">
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono-x">{q.number}</span>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#3B4157]">{q.type === "options" ? "Su priedais" : "Parinkimas pagal lipduką"}</span>
        <span data-testid={`inquiry-status-${q.id}`} className={`text-xs ${STATUS[q.status][1]}`}>{STATUS[q.status][0]}{q.status === "approved" && q.offer_valid_until && ` · galioja iki ${q.offer_valid_until.slice(0, 10)}`}</span>
        {q.status === "approved" && q.offer_valid_until && q.offer_valid_until < new Date().toISOString() && <span data-testid={`inquiry-expired-${q.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-red-400 text-red-400">Pasiūlymas baigėsi</span>}
      </div>
      <div className="text-xs text-slate-500">{new Date(q.created_at).toLocaleString("lt-LT")}</div>
      {q.product && (
        <div>
          <div>{q.product.name} — <span className="font-mono-x">{q.total == null ? "Klausti kainos" : <>{eur(q.product.price)} <span className="text-[10px] text-slate-500">be PVM</span></>}</span></div>
          {q.options.map((o) => <div key={o.key} className="text-slate-400 text-xs">+ {o.name} — {eur(o.price)}</div>)}
          {q.product.fits && <div className="text-xs text-slate-400">Markė: {q.product.fits} · {q.product.sku}</div>}
          <div className="font-display font-black text-xl text-orange-500 mt-1">{q.total == null ? "Klausti kainos" : <>{eur(q.total)} <span className="text-[10px] text-slate-500">be PVM</span></>}</div>
        </div>
      )}
      {q.machine && <div><span className="text-slate-500">Technika:</span> {q.machine}</div>}
      {q.message && <div className="italic text-slate-300">„{q.message}“</div>}
      {q.photos?.length > 0 && (
        <div className="flex gap-2 flex-wrap">{q.photos.map((p, i) => (
          <button key={p} data-testid={`inquiry-photo-${q.id}-${i}`} onClick={() => onZoom(p)} className="w-20 h-20 border border-[#3B4157] hover:border-orange-500"><img src={imgUrl(p)} alt="" className="w-full h-full object-cover" /></button>
        ))}</div>
      )}
      {q.offer_total && <div className="text-xs text-sky-400">Pasiūlyta kaina: {eur(q.offer_total)} (su PVM)</div>}
    </div>
    <Customer c={q.customer} />
    <div className="flex lg:flex-col gap-2">
      {["new", "approved"].includes(q.status) && <button data-testid={`inquiry-approve-btn-${q.id}`} onClick={() => onApprove(q)} className="h-10 px-4 bg-orange-500 text-[#1F2230] text-sm font-display font-bold uppercase tracking-wider hover:bg-orange-400">{q.status === "approved" ? "Siųsti pasiūlymą iš naujo" : "Siųsti pasiūlymą"}</button>}
      {q.status === "new" && <button data-testid={`inquiry-reject-btn-${q.id}`} onClick={() => window.confirm("Atmesti užklausą?") && onStatus(q, "rejected")} className="h-10 px-4 border border-[#3B4157] text-sm hover:border-red-400 hover:text-red-400">Atmesti</button>}
      {["rejected", "paid"].includes(q.status) && <button data-testid={`inquiry-close-btn-${q.id}`} onClick={() => onStatus(q, "closed")} className="h-10 px-4 border border-[#3B4157] text-sm hover:border-slate-400">Uždaryti</button>}
    </div>
  </div>
);

export default function AdminInquiries() {
  const [list, setList] = useState([]);
  const [approve, setApprove] = useState(null);
  const [zoom, setZoom] = useState(null);
  const load = () => api.get("/admin/inquiries").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (q, status) => {
    await api.patch(`/admin/inquiries/${q.id}`, { status });
    toast.success("Būsena atnaujinta");
    load();
  };

  return (
    <div data-testid="admin-inquiries" className="space-y-6">
      <h1 className="font-display font-black uppercase text-4xl tracking-tight">Užklausos</h1>
      <p className="text-sm text-slate-400">Sėdynės su priedais ir parinkimas pagal lipduką. Patvirtinus, klientui išsiunčiama apmokėjimo nuoroda (pristatymas iki 14 d.).</p>
      {list.length === 0 && <div className="border border-[#3B4157] p-8 text-center text-slate-500">Užklausų dar nėra</div>}
      <div className="space-y-4">{list.map((q) => <Card key={q.id} q={q} onApprove={setApprove} onStatus={setStatus} onZoom={setZoom} />)}</div>
      {approve && <ApproveDialog inq={approve} onClose={() => setApprove(null)} onDone={() => { setApprove(null); load(); }} />}
      {zoom && (
        <button data-testid="inquiry-photo-zoom" onClick={() => setZoom(null)} className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6">
          <img src={imgUrl(zoom)} alt="" className="max-w-full max-h-full object-contain" />
        </button>
      )}
    </div>
  );
}
